import { CopyOutlined, DeleteOutlined, EditOutlined } from '@ant-design/icons';
import {
  ActionButton,
  LoadingIcon,
  ModalConfirmDelete,
  PageContainer,
  ActionButtonMobile,
} from '@you-go-vn/components';
import { Card, message, Space, Button, Table, Menu, Tooltip, Row } from 'antd';
import { ColumnsType, TablePaginationConfig } from 'antd/lib/table';
import { useCallback, useEffect, useState } from 'react';
import DetailModal from '../components/DetailModal';
import ModalFormPromotion from '../components/ModalFormPromotion';
import { promotionServices } from './services';
import { InputSearch } from '@you-go-vn/components/src/Common/Input/Input';
import useDebounce from '@you-go-vn/components/hooks/useDebounce';
import SaleIcon from '@you-go-vn/components/src/Common/Icons/SaleIcon';
import FreeShipIcon from '@you-go-vn/components/src/Common/Icons/FreeShipIcon';

import { PromotionDetail } from '../data';
import { useAppSelector } from '../../../store';
import { promotionDiscountTargets } from '..';

const pageName = 'Khuyến mãi';
const initPagination: TablePaginationConfig = {
  current: 1,
  pageSize: 10,
  total: 0,
};

const RestaurantPromotionsPage = () => {
  const { currentRestaurant } = useAppSelector((s) => s.restaurant);
  const [data, setData] = useState<PromotionDetail[]>([]);
  const [valueSearch, setValueSearch] = useState('');
  const [pagination, setPagination] = useState(initPagination);

  const [loading, setLoading] = useState(true);
  const [loadingTable, setLoadingTable] = useState(true);

  const [currentRow, setCurrentRow] = useState<PromotionDetail>();

  const [detailModalVisible, setDetailModalVisible] = useState(false);
  const [addModalVisible, setAddModalVisible] = useState<boolean>(false);
  const [updateModalVisible, setUpdateModalVisible] = useState<boolean>(false);
  const [deleteModalVisible, setDeleteModalVisible] = useState(false);
  const [copiedModalVisible, setCopiedModalVisible] = useState<boolean>(false);

  const fetchTable = useCallback(
    ({ pagination, value } = { pagination: initPagination, value: '' }) => {
      setLoadingTable(true);
      promotionServices
        .get(currentRestaurant?.id, {
          page: pagination.current,
          limit: pagination.pageSize,
          keywords: value ? value : undefined,
        })
        .then(async ({ data }) => {
          const items = data.data.items;
          setData(items);
          setPagination({
            ...pagination,
            total: data.data.total,
          });
          setLoadingTable(false);
        });
    },
    [currentRestaurant],
  );
  const search = useDebounce(fetchTable);
  useEffect(() => {
    async function fetch() {
      await Promise.all([fetchTable()]);
      setLoading(false);
    }
    fetch();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const menu = (
    <Menu>
      <Menu.Item key={0} onClick={() => setAddModalVisible(true)}>
        Thêm mới {pageName}
      </Menu.Item>
    </Menu>
  );
  const ActionGroup = ({ record }: { record?: PromotionDetail }) => {
    if (!record) return <></>;
    return (
      <Space wrap={false}>
        <Button
          key="config"
          className="light positive"
          size="small"
          block
          icon={<CopyOutlined />}
          onClick={(e) => {
            e.stopPropagation();
            setCopiedModalVisible(true);
            setCurrentRow(record);
          }}
        >
          Sao chép
        </Button>
        <Button
          key="config"
          className="light action"
          size="small"
          block
          icon={<EditOutlined />}
          onClick={(e) => {
            e.stopPropagation();
            setUpdateModalVisible(true);
            setCurrentRow(record);
          }}
        >
          Chỉnh sửa
        </Button>
        <Button
          key="subscribeAlert"
          className="light danger"
          block
          size="small"
          icon={<DeleteOutlined />}
          onClick={(e) => {
            e.stopPropagation();
            setDeleteModalVisible(true);
            setCurrentRow(record);
          }}
        >
          Xoá bỏ
        </Button>
      </Space>
    );
  };
  const columns: ColumnsType<PromotionDetail> = [
    {
      width: 30,
      render: (_, record) => (
        <Tooltip
          title={
            promotionDiscountTargets.find(
              (x) => x.value === record.discountTarget,
            )?.label || 'Chưa cập nhật'
          }
        >
          {record.discountTarget === 'base_total' ? (
            <SaleIcon style={{ fontSize: 40, color: 'red' }} />
          ) : (
            <FreeShipIcon style={{ fontSize: 40, color: 'yellowgreen' }} />
          )}
        </Tooltip>
      ),
    },
    {
      title: 'Tên',
      dataIndex: 'name',
    },
    {
      title: 'Mã khuyến mãi',
      dataIndex: 'code',
    },
    { title: 'Mô tả', dataIndex: 'description', responsive: ['xl'] },

    {
      title: 'Tuỳ chọn',
      dataIndex: 'option',
      responsive: ['lg'],
      align: 'right',
      width: 250,
      render: (_, record) => <ActionGroup record={record} />,
    },
  ];

  return (
    <>
      <PageContainer
        heading={pageName}
        extraHeading={<ActionButtonMobile menu={menu} />}
      >
        {loading ? (
          <LoadingIcon />
        ) : (
          <>
            <Card
              title={
                <Row align="middle">
                  Danh sách {pageName}
                  <ActionButton menu={menu} />
                </Row>
              }
              extra={
                <Space>
                  <InputSearch
                    onChange={(e) => {
                      setValueSearch(e.currentTarget.value);
                      search({
                        pagination: initPagination,
                        value: e.currentTarget.value,
                      });
                    }}
                    placeholder="Tìm kiếm"
                  />
                </Space>
              }
            >
              <Table<PromotionDetail>
                columns={columns}
                rowKey={(record) => record.id}
                dataSource={data}
                pagination={pagination}
                loading={loadingTable}
                onChange={(pagination) => {
                  fetchTable({ pagination, value: valueSearch });
                }}
                onRow={(record) => {
                  return {
                    onClick: () => {
                      setCurrentRow(record);
                      setDetailModalVisible(true);
                    },
                  };
                }}
              />
            </Card>

            {currentRow && (
              <DetailModal
                visible={detailModalVisible}
                initialValues={currentRow}
                onCancel={() => {
                  setDetailModalVisible(false);
                  setCurrentRow(undefined);
                }}
                actions={<ActionGroup record={currentRow} />}
              />
            )}

            <ModalFormPromotion
              visible={addModalVisible}
              title={`Thêm mới ${pageName}`}
              onCancel={() => {
                setAddModalVisible(false);
              }}
              onFinish={async (values) => {
                const hideMessage = message.loading('Đang thêm mới', 0);
                try {
                  const res = await promotionServices.add(
                    currentRestaurant?.id,
                    values,
                  );
                  if (res.data && res.data.message === 'success') {
                    message.success('Thành công');
                    setAddModalVisible(false);
                    // reload table
                    fetchTable();
                    return { success: true };
                  } else if (res.data.errors) {
                    message.error('Đã xảy ra lỗi, vui lòng kiểm tra lại!');
                    return { errors: res.data.errors };
                  } else {
                    throw new Error();
                  }
                } catch (error) {
                  message.error('Đã xảy ra lỗi, vui lòng kiểm tra lại!');
                  return {};
                } finally {
                  hideMessage();
                }
              }}
            />

            <ModalFormPromotion
              visible={updateModalVisible}
              title={`Cập nhật ${pageName} "${currentRow?.name}"`}
              initValues={currentRow}
              onCancel={() => {
                setUpdateModalVisible(false);
                if (!detailModalVisible) setCurrentRow(undefined);
              }}
              onFinish={async (values) => {
                if (!currentRow) return {};
                const hideMessage = message.loading('Đang cập nhật');
                try {
                  const res = await promotionServices.update(
                    currentRestaurant?.id,
                    currentRow.id,
                    values,
                  );
                  if (res.data.message === 'success') {
                    message.success('Thành công');
                    setUpdateModalVisible(false);
                    if (!detailModalVisible) {
                      setCurrentRow(undefined);
                    } else {
                      setCurrentRow({ ...values });
                    }
                    fetchTable();
                    return { success: true };
                  } else if (res.data.errors) {
                    message.error('Đã xảy ra lỗi, vui lòng kiểm tra lại!');
                    return { errors: res.data.errors };
                  } else {
                    throw new Error();
                  }
                } catch (error) {
                  message.error('Đã xảy ra lỗi, vui lòng kiểm tra lại!');
                  return {};
                } finally {
                  hideMessage();
                }
              }}
            />

            <ModalConfirmDelete
              title={`Xoá "${currentRow?.name}"`}
              deleteModalVisible={deleteModalVisible}
              onConfirm={async () => {
                if (!currentRow) return;
                const hideMessage = message.loading('Đang xoá');
                try {
                  await promotionServices.remove(
                    currentRestaurant?.id,
                    currentRow.id,
                  );
                  hideMessage();
                  message.success('Thành công');
                  setDetailModalVisible(false);
                  setDeleteModalVisible(false);
                  setCurrentRow(undefined);
                  fetchTable();
                } catch (error) {
                  hideMessage();
                  message.error('Đã xảy ra lỗi, vui lòng kiểm tra lại!');
                }
              }}
              onCancel={() => {
                setDeleteModalVisible(false);
                if (!detailModalVisible) setCurrentRow(undefined);
              }}
            />

            <ModalFormPromotion
              visible={copiedModalVisible}
              title={`Thêm mới ${pageName}`}
              initValues={currentRow}
              onCancel={() => {
                setCopiedModalVisible(false);
              }}
              onFinish={async (values) => {
                const hideMessage = message.loading('Đang thêm mới', 0);
                try {
                  const res = await promotionServices.add(
                    currentRestaurant?.id,
                    values,
                  );
                  if (res.data && res.data.message === 'success') {
                    message.success('Thành công');
                    setCopiedModalVisible(false);
                    // reload table
                    fetchTable();
                    return { success: true };
                  } else if (res.data.errors) {
                    message.error('Đã xảy ra lỗi, vui lòng kiểm tra lại!');
                    return { errors: res.data.errors };
                  } else {
                    throw new Error();
                  }
                } catch (error) {
                  message.error('Đã xảy ra lỗi, vui lòng kiểm tra lại!');
                  return {};
                } finally {
                  hideMessage();
                }
              }}
            />
          </>
        )}
      </PageContainer>
    </>
  );
};

export default RestaurantPromotionsPage;
